import { HourlyForecastTimelineData } from '../models/HourlyForecastTimelineData';
import {
  FOG_800M_COLOR,
  FOG_1600M_COLOR,
  FOG_3000M_COLOR,
  NO_FOG_COLOR,
  NO_DATA_COLOR
} from '../theme/colors';

// Visibility thresholds in metres
const VISIBILITY_THRESHOLD_800M = 800;
const VISIBILITY_THRESHOLD_1600M = 1600;
const VISIBILITY_THRESHOLD_3000M = 3000;

// For 30-min segment timelines, these are the indices (0-based) of the freshest segment per horizon
const LATEST_HORIZON_60_SEGMENT = 1;
const LATEST_HORIZON_120_SEGMENT = 3;
const LATEST_HORIZON_180_SEGMENT = 5;
const LATEST_HORIZON_240_SEGMENT = 7;

// Re-export for tests
export {
  FOG_800M_COLOR,
  FOG_1600M_COLOR,
  FOG_3000M_COLOR,
  NO_FOG_COLOR,
  NO_DATA_COLOR,
  VISIBILITY_THRESHOLD_800M,
  VISIBILITY_THRESHOLD_1600M,
  VISIBILITY_THRESHOLD_3000M
};

export interface AirportStatus {
  actualColor: string;
  forecastColor: string;
}

export interface RankedAirport {
  id: number;
  code: string;
  name: string;
  severity: number; // Forecast severity: 0 = most severe (red), 3 = least severe (green)
  actualSeverity: number;
  forecastSeverity: number;
  status: AirportStatus;
}

/**
 * Service for calculating fog status from timeline data
 */
export class FogStatusCalculator {
  
  /**
   * Convert visibility value to color
   */
  static getVisibilityColor(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(value)) return NO_DATA_COLOR;
    if (value <= VISIBILITY_THRESHOLD_800M) return FOG_800M_COLOR;
    if (value <= VISIBILITY_THRESHOLD_1600M) return FOG_1600M_COLOR;
    if (value <= VISIBILITY_THRESHOLD_3000M) return FOG_3000M_COLOR;
    return NO_FOG_COLOR;
  }
  
  /**
   * Get severity level from color (lower = more severe)
   */
  static getColorSeverity(color: string): number {
    switch(color) {
      case FOG_800M_COLOR: return 0;   // Most severe
      case FOG_1600M_COLOR: return 1;
      case FOG_3000M_COLOR: return 2;
      case NO_FOG_COLOR: return 3;
      case NO_DATA_COLOR: return 4;
      default: return 5;
    }
  }
  
  /**
   * Calculate actual status from the two most recent segments
   * Returns the worst visibility of the two
   */
  static calculateActualStatus(timelineData: HourlyForecastTimelineData): string {
    // Get actual data segments only
    const actualSegments = timelineData.segments
      .filter(s => s.raw_actuals !== null)
      .sort((a, b) => b.time.getTime() - a.time.getTime()); // Sort by time descending (most recent first)
    
    if (actualSegments.length === 0) {
      return NO_DATA_COLOR;
    }
    
    // Take the two most recent segments
    const recentSegments = actualSegments.slice(0, 2);
    
    // Find the worst visibility among them
    let worstVisibility: number | null = null;
    
    for (const segment of recentSegments) {
      const visibility = segment.visibility_level;
      if (visibility !== null && visibility !== undefined) {
        if (worstVisibility === null || visibility < worstVisibility) {
          worstVisibility = visibility;
        }
      }
    }
    
    // If we found no valid visibility values, return no data
    if (worstVisibility === null) {
      return NO_DATA_COLOR;
    }
    
    return this.getVisibilityColor(worstVisibility);
  }

  /**
   * Calculate forecast status for 60-minute segments
   * Any single segment with low visibility triggers the status
   */
  static calculateForecastStatus60Min(forecastSegments: Array<{ time: Date; visibility_level: number | null }>): string {
    let worstVisibility: number | null = null;

    for (const segment of forecastSegments) {
      const visibility = segment.visibility_level;

      if (visibility !== null && visibility <= VISIBILITY_THRESHOLD_3000M) {
        if (worstVisibility === null || visibility < worstVisibility) {
          worstVisibility = visibility;
        }
      }
    }

    if (worstVisibility === null) {
      return NO_FOG_COLOR;
    }

    return this.getVisibilityColor(worstVisibility);
  }

  /**
   * Calculate forecast status for 30-minute segments (UK/EU airports)
   * Checks the freshest segment per horizon (the latest 30-min segment in each hour)
   * Returns worst visibility if any of these segments has low visibility
   */
  static calculateForecastStatus30Min(forecastSegments: Array<{ time: Date; visibility_level: number | null }>): string {
    const latestSegmentPerHorizon = [
      LATEST_HORIZON_60_SEGMENT,
      LATEST_HORIZON_120_SEGMENT,
      LATEST_HORIZON_180_SEGMENT,
      LATEST_HORIZON_240_SEGMENT
    ];

    let worstVisibility: number | null = null;

    for (const index of latestSegmentPerHorizon) {
      if (index < forecastSegments.length) {
        const segment = forecastSegments[index];
        const visibility = segment.visibility_level;

        if (visibility !== null && visibility <= VISIBILITY_THRESHOLD_3000M) {
          if (worstVisibility === null || visibility < worstVisibility) {
            worstVisibility = visibility;
          }
        }
      }
    }

    if (worstVisibility === null) {
      return NO_FOG_COLOR;
    }

    return this.getVisibilityColor(worstVisibility);
  }

  /**
   * Calculate forecast status for 15-minute segments (NZ airports)
   * Requires two consecutive segments with low visibility within the same horizon
   */
  static calculateForecastStatus15Min(forecastSegments: Array<{ time: Date; visibility_level: number | null; raw_predictions: { horizon: number } | null }>): string {
    if (forecastSegments.length < 2) {
      return NO_FOG_COLOR;
    }

    // Group segments by horizon, preserving time order
    const segmentsByHorizon = new Map<number, Array<{ time: Date; visibility_level: number | null }>>();

    for (const segment of forecastSegments) {
      const horizon = segment.raw_predictions?.horizon;
      if (horizon === undefined || horizon === null) continue;

      if (!segmentsByHorizon.has(horizon)) {
        segmentsByHorizon.set(horizon, []);
      }
      segmentsByHorizon.get(horizon)!.push(segment);
    }

    let worstVisibility: number | null = null;

    // Check each horizon for two consecutive low visibility segments
    for (const [, segments] of segmentsByHorizon) {
      // Sort by time within horizon
      segments.sort((a, b) => a.time.getTime() - b.time.getTime());

      // Check for consecutive low visibility segments
      for (let i = 0; i < segments.length - 1; i++) {
        const current = segments[i];
        const next = segments[i + 1];

        const currentLowVis = current.visibility_level !== null && current.visibility_level <= VISIBILITY_THRESHOLD_3000M;
        const nextLowVis = next.visibility_level !== null && next.visibility_level <= VISIBILITY_THRESHOLD_3000M;

        if (currentLowVis && nextLowVis) {
          // Found consecutive low vis - track worst visibility
          const currentVis = current.visibility_level!;
          const nextVis = next.visibility_level!;
          const pairWorst = Math.min(currentVis, nextVis);

          if (worstVisibility === null || pairWorst < worstVisibility) {
            worstVisibility = pairWorst;
          }
        }
      }
    }

    if (worstVisibility === null) {
      return NO_FOG_COLOR;
    }

    return this.getVisibilityColor(worstVisibility);
  }

  /**
   * Error codes that indicate data is too incomplete to show a forecast status
   */
  private static readonly EXCESSIVE_MISSING_ERROR_CODES = [
    'EXCESSIVE_MISSING_PREDICTIONS',
    'EXCESSIVE_MISSING_PREDICTIONS_30MIN',
    'EXCESSIVE_MISSING_PREDICTIONS_HOURLY'
  ];

  /**
   * Calculate forecast status based on segment interval
   * - 60-minute segments: Any single low visibility segment triggers status
   * - 30-minute segments: Check freshest segment per horizon for any low visibility
   * - 15-minute segments: Require two consecutive low visibility segments within the same horizon
   */
  static calculateForecastStatus(timelineData: HourlyForecastTimelineData): string {
    // Check for excessive missing data errors - return no data if present
    if (timelineData.errors?.some(e => this.EXCESSIVE_MISSING_ERROR_CODES.includes(e.code))) {
      return NO_DATA_COLOR;
    }

    const now = new Date();
    const segmentInterval = timelineData.segment_interval;

    // Get current time rounded down to nearest segment boundary
    const minutes = now.getMinutes();
    const currentSegmentStart = Math.floor(minutes / segmentInterval) * segmentInterval;
    const startTime = new Date(now);
    startTime.setMinutes(currentSegmentStart);
    startTime.setSeconds(0);
    startTime.setMilliseconds(0);

    // Calculate end time (4 hours from start)
    const endTime = new Date(startTime);
    endTime.setHours(endTime.getHours() + 4);

    // Get forecast segments within time window, sorted by time
    const forecastSegments = timelineData.segments
      .filter(s => {
        if (!s.raw_predictions) return false;
        const segmentTime = new Date(s.time);
        return segmentTime >= startTime && segmentTime < endTime;
      })
      .sort((a, b) => a.time.getTime() - b.time.getTime());

    if (forecastSegments.length === 0) {
      console.log(`[FogStatus] ${timelineData.location_name} (${segmentInterval}min): NO FORECAST SEGMENTS - total segments: ${timelineData.segments.length}, with raw_predictions: ${timelineData.segments.filter(s => s.raw_predictions).length}`);
      return NO_DATA_COLOR;
    }

    // Debug: log filtered forecast segments for each location
    console.log(`[FogStatus] ${timelineData.location_name} (${segmentInterval}min): ${forecastSegments.map((s, i) => `[${i}] ${s.time.toISOString().slice(11, 16)} vis=${s.visibility_level}`).join(', ')}`);

    if (segmentInterval === 60) {
      // 60-minute segments: any single low visibility segment triggers status
      return this.calculateForecastStatus60Min(forecastSegments);
    } else if (segmentInterval === 30) {
      // 30-minute segments: check freshest segment per horizon
      return this.calculateForecastStatus30Min(forecastSegments);
    } else {
      // 15-minute segments: require two low vis segments within same horizon
      return this.calculateForecastStatus15Min(forecastSegments);
    }
  }
  
  /**
   * Calculate both actual and forecast status for an airport
   */
  static calculateAirportStatuses(timelineData: HourlyForecastTimelineData): AirportStatus {
    return {
      actualColor: this.calculateActualStatus(timelineData),
      forecastColor: this.calculateForecastStatus(timelineData)
    };
  }
  
  /**
   * Rank airports by forecast severity, then actual severity, then alphabetically by name
   */
  static rankAirportsBySeverity(
    airports: Array<{ id: number; code: string; name: string; status: AirportStatus }>
  ): RankedAirport[] {
    return airports
      .map(airport => {
        const actualSeverity = this.getColorSeverity(airport.status.actualColor);
        const forecastSeverity = this.getColorSeverity(airport.status.forecastColor);

        return {
          id: airport.id,
          code: airport.code,
          name: airport.name,
          severity: forecastSeverity, // Use forecast severity as primary for RankedAirport interface
          actualSeverity,
          forecastSeverity,
          status: airport.status
        };
      })
      .sort((a, b) => {
        // Primary: Sort by forecast severity (lower = more severe)
        if (a.forecastSeverity !== b.forecastSeverity) {
          return a.forecastSeverity - b.forecastSeverity;
        }
        // Secondary: Sort by actual severity (lower = more severe)
        if (a.actualSeverity !== b.actualSeverity) {
          return a.actualSeverity - b.actualSeverity;
        }
        // Tertiary: Sort alphabetically by airport name
        return a.name.localeCompare(b.name);
      });
  }
}